/**
 * 母国語・学習言語と、旗用の代表地域（ISO 3166-1 alpha-2）
 * 言語名・国名は Intl.DisplayNames（日本語 / 英語）に依存
 */

/** 言語コード → 代表地域（ISO 3166-1 alpha-2）。旗・国名表示に利用 */
const LANG_TO_REGION = {
  aa: 'ET', ab: 'GE', ae: 'IR', af: 'ZA', ak: 'GH', am: 'ET', an: 'ES', ar: 'SA', as: 'IN', av: 'RU', ay: 'BO', az: 'AZ',
  // ca=カタルーニャ語 → AD(アンドラ: 唯一の公用語)
  // cu=教会スラヴ語  → BG(ブルガリア: 語源・ブルガリア正教会で使用)
  // ee=エウェ語      → TG(トーゴ: 主要国民語。ak/tw が GH を使用中)
  ba: 'RU', be: 'BY', bg: 'BG', bi: 'VU', bm: 'ML', bn: 'BD', bo: 'CN', br: 'FR', bs: 'BA', ca: 'AD', ce: 'RU', ch: 'GU',
  co: 'FR', cr: 'CA', cs: 'CZ', cu: 'BG', cv: 'RU', cy: 'GB', da: 'DK', de: 'DE', dv: 'MV', dz: 'BT', ee: 'TG', el: 'GR',
  // ff=フラニ語 → GN(ギニア: 公用語の一つ。wo が SN を使用中)
  en: 'US', es: 'ES', et: 'EE', eu: 'ES', fa: 'IR', ff: 'GN', fi: 'FI', fj: 'FJ', fo: 'FO', fr: 'FR', fy: 'NL', ga: 'IE',
  gd: 'GB', gl: 'ES', gn: 'PY', gu: 'IN', gv: 'IM', ha: 'NG', he: 'IL', hi: 'IN', ho: 'PG', hr: 'HR', ht: 'HT', hu: 'HU',
  hy: 'AM', hz: 'NA', id: 'ID', ig: 'NG', ii: 'CN', ik: 'US', is: 'IS', it: 'IT', iu: 'CA', ja: 'JP', jv: 'ID', ka: 'GE',
  // kr=カヌリ語 → NE(ニジェール: 主要言語。ha/ig/yo が NG を使用中)
  // ks=カシミール語 → PK(パキスタン側カシミールで主要言語)
  kg: 'CD', ki: 'KE', kj: 'NA', kk: 'KZ', kl: 'GL', km: 'KH', kn: 'IN', ko: 'KR', kr: 'NE', ks: 'PK', ku: 'IQ', kv: 'RU',
  kw: 'GB', ky: 'KG', lb: 'LU', lg: 'UG', li: 'NL', ln: 'CD', lo: 'LA', lt: 'LT', lu: 'CD', lv: 'LV', mg: 'MG', mh: 'MH',
  mi: 'NZ', mk: 'MK', ml: 'IN', mn: 'MN', mr: 'IN', ms: 'MY', mt: 'MT', my: 'MM', na: 'NR', nb: 'NO', nd: 'ZW', ne: 'NP',
  // om=オロモ語 → KE(ケニア: 広く話される。aa/am が ET を使用中)
  ng: 'NA', nl: 'NL', nn: 'NO', no: 'NO', nr: 'ZA', nv: 'US', ny: 'MW', oc: 'FR', oj: 'CA', om: 'KE', or: 'IN', os: 'GE',
  pa: 'IN', pl: 'PL', ps: 'AF', pt: 'BR', qu: 'PE', rm: 'CH', rn: 'BI', ro: 'RO', ru: 'RU', rw: 'RW', sa: 'IN', sc: 'IT',
  // se=北サーミ語 → SE(スウェーデン: nb/nn/no が NO を使用中)
  // ta=タミル語    → LK(スリランカ: 公用語の一つ。hi/ml 等で IN は十分)
  sd: 'PK', se: 'SE', sg: 'CF', si: 'LK', sk: 'SK', sl: 'SI', sm: 'WS', sn: 'ZW', so: 'SO', sq: 'AL', sr: 'RS', ss: 'SZ',
  st: 'LS', su: 'ID', sv: 'SE', sw: 'TZ', ta: 'LK', te: 'IN', tg: 'TJ', th: 'TH', ti: 'ER', tk: 'TM', tl: 'PH', tn: 'BW',
  to: 'TO', tr: 'TR', ts: 'ZA', tt: 'RU', tw: 'GH', ty: 'PF', ug: 'CN', uk: 'UA', ur: 'PK', uz: 'UZ', ve: 'ZA', vi: 'VN',
  // yi=イディッシュ語 → IL(イスラエル: 主要話者国)
  wa: 'BE', wo: 'SN', xh: 'ZA', yi: 'IL', yo: 'NG', za: 'CN', zh: 'CN', zu: 'ZA', bh: 'IN',
};

/**
 * 複数の言語が同じ地域にマップされている場合の代表言語（明示オーバーライド）
 * ルール: 最も話者数が多い/最も国を代表する公用語を選択
 */
const REGION_PRIMARY_LANG = {
  BG: 'bg',  // ブルガリア語（cu=教会スラヴ語より優先）
  CA: 'iu',  // イヌクティトゥット語（カナダ先住民語。en/fr はUS/FRにマップ）
  CD: 'ln',  // リンガラ語（コンゴ民主共和国の共通語）
  CN: 'zh',  // 中国語（普通話）
  ES: 'es',  // スペイン語（an/eu/gl より優先）
  ET: 'am',  // アムハラ語（エチオピアの公用語）
  FR: 'fr',  // フランス語（br/co/oc より優先）
  GB: 'cy',  // ウェールズ語（en は US にマップ済み）
  GE: 'ka',  // ジョージア語（ab/os より優先）
  GH: 'ak',  // アカン語（ガーナ最大の言語群）
  ID: 'id',  // インドネシア語（jv/su より優先）
  IL: 'he',  // ヘブライ語（yi=イディッシュ語より優先）
  IN: 'hi',  // ヒンディー語（インド最大の公用語）
  IR: 'fa',  // ペルシア語（ae=古代語より優先）
  IT: 'it',  // イタリア語（sc=サルデーニャ語より優先）
  KE: 'ki',  // キクユ語（ケニア最大の民族語）
  LK: 'si',  // シンハラ語（スリランカ多数派言語）
  NA: 'ng',  // ンドンガ語（ナミビア北部の主要言語）
  NG: 'ha',  // ハウサ語（ナイジェリア最大話者数）
  NL: 'nl',  // オランダ語（fy/li より優先）
  NO: 'no',  // ノルウェー語（nb/nn より優先）
  PK: 'ur',  // ウルドゥー語（パキスタンの国語）
  RU: 'ru',  // ロシア語
  SE: 'sv',  // スウェーデン語（se=北サーミ語より優先）
  US: 'en',  // 英語
  ZA: 'zu',  // ズールー語（南アフリカ最大民族語）
  ZW: 'sn',  // ショナ語（ジンバブエ最大民族語）
};

function regionToFlag(region) {
  if (!region || region.length !== 2) return '🌐';
  const A = 0x1f1e6;
  const up = region.toUpperCase();
  const a = up.charCodeAt(0) - 65;
  const b = up.charCodeAt(1) - 65;
  if (a < 0 || a > 25 || b < 0 || b > 25) return '🌐';
  return String.fromCodePoint(A + a, A + b);
}

/** ISO 3166-1 alpha-2 → 日本語の国・地域名 */
export function regionNameJa(iso3166) {
  if (!iso3166 || iso3166.length !== 2) return '';
  const up = iso3166.toUpperCase();
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      const dn = new Intl.DisplayNames(['ja'], { type: 'region' });
      const s = dn.of(up);
      if (s) return s;
    }
  } catch { /* ignore */ }
  return up;
}

/** ISO 3166-1 alpha-2 → 英語の国・地域名 */
export function regionNameEn(iso3166) {
  if (!iso3166 || iso3166.length !== 2) return '';
  const up = iso3166.toUpperCase();
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      const dn = new Intl.DisplayNames(['en'], { type: 'region' });
      const s = dn.of(up);
      if (s) return s;
    }
  } catch { /* ignore */ }
  return up;
}

/** 言語コード → 国旗絵文字 */
export function langFlag(code) {
  if (!code) return '🌐';
  const c = String(code).toLowerCase().slice(0, 2);
  const r = LANG_TO_REGION[c];
  return r ? regionToFlag(r) : '🌐';
}

/** 言語コード → 日本語言語名 */
export function langNameJa(code) {
  if (!code) return '—';
  const c = String(code).toLowerCase().slice(0, 8);
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
      const dn = new Intl.DisplayNames(['ja'], { type: 'language', languageDisplay: 'standard' });
      const name = dn.of(c);
      if (name) return name;
    }
  } catch { /* ignore */ }
  return c;
}

/** 言語に紐づく代表地域の日本語名 */
export function langRegionNameJa(langCode) {
  const c = String(langCode || '').toLowerCase().slice(0, 2);
  const r = LANG_TO_REGION[c];
  return r ? regionNameJa(r) : '';
}

/** 読み上げ用 BCP 47 タグ */
export function speechBcp47(code) {
  const part = String(code || 'ja').toLowerCase().split(/[-_]/)[0].slice(0, 8);
  const r = LANG_TO_REGION[part];
  if (r && r.length === 2) return `${part}-${r}`;
  return part.length ? part : 'ja';
}

/** 言語コード → 地域コード（ISO 3166-1 alpha-2） */
export function langToRegion(langCode) {
  if (!langCode) return '';
  return LANG_TO_REGION[String(langCode).toLowerCase().slice(0, 2)] || '';
}

/** 地域コード → 代表言語コード */
export function langFromRegion(regionCode) {
  if (!regionCode) return '';
  const up = regionCode.toUpperCase();
  if (REGION_PRIMARY_LANG[up]) return REGION_PRIMARY_LANG[up];
  return Object.entries(LANG_TO_REGION).find(([, r]) => r === up)?.[0] || '';
}

/**
 * 国選択用オプション一覧（英語アルファベット順）
 * LANG_TO_REGION に登録されている全地域をユニークに返す
 */
export function countryOptions() {
  const regionSet = new Set(Object.values(LANG_TO_REGION));
  return Array.from(regionSet)
    .map((regionCode) => ({
      regionCode,
      langCode: langFromRegion(regionCode),
      nameEn: regionNameEn(regionCode),
      flag: regionToFlag(regionCode),
    }))
    .sort((a, b) =>
      a.nameEn.localeCompare(b.nameEn, 'en', { sensitivity: 'base' })
    );
}
